// ManagerSettingDialog.jsx
import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Snackbar,
  Alert,
  useTheme,
  Box,
  InputAdornment,
  IconButton,
  Typography,
  Checkbox,
  FormControlLabel,
  Chip,
} from "@mui/material";
import axios from "axios";
import {
  AccountCircle,
  Cancel,
  CheckCircle,
  EmailRounded,
  Lock,
  Phone,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import { tokens } from "../theme";

const CMTSettingDialog = ({
  open,
  onClose,
  cmt = {},
  managerId,
  onSave,
  onChangeStatus,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [formData, setFormData] = useState({});
  const [initialFormData, setInitialFormData] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [isSubmit, setIsSubmit] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const defaultData = {
      username: cmt?.username || "",
      email: cmt?.email || "",
      phone: cmt?.phone || "",
      status: cmt?.status || "pending",
      password: "",
      confirmPassword: "",
      permissions: {
        canAccessPortal: cmt?.permissions?.canAccessPortal || false,
        canAddTenantLandlord: cmt?.permissions?.canAddTenantLandlord || false,
        canAddProvider: cmt?.permissions?.canAddProvider || false,
        canAddUnit: cmt?.permissions?.canAddUnit || false,
        canAnnouncement: cmt?.permissions?.canAnnouncement || false,
        canReport: cmt?.permissions?.canReport || false,
        canFinancial: cmt?.permissions?.canFinancial || false,
        canSubscription: cmt?.permissions?.canSubscription || false,
      },
    };

    setFormData(defaultData);
    setInitialFormData(defaultData);
  }, [cmt?.id, open]);

  // --- Highlight helpers ---
  const isFieldChanged = (field) => {
    if (!initialFormData) return false;
    return formData[field] !== initialFormData[field];
  };
  const isPermissionChanged = (perm) => {
    if (!initialFormData) return false;
    return (
      formData.permissions &&
      initialFormData.permissions &&
      formData.permissions[perm] !== initialFormData.permissions[perm]
    );
  };

  // ------------------------
  const handleStatusChange = async (newStatus) => {
    if (newStatus !== formData.status) {
      if (onChangeStatus) {
        setStatusLoading(true);
        try {
          await onChangeStatus(newStatus);
          setFormData((prev) => ({
            ...prev,
            status: newStatus,
          }));
        } catch (err) {
          // Optional: handle error locally
        } finally {
          setStatusLoading(false);
        }
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePermissionChange = (key, checked) => {
    setFormData((prev) => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [key]: checked,
      },
    }));
  };

  const hasFormChanged = () => {
    if (!initialFormData) return false;
    const clean = (data) => {
      const { password, confirmPassword, ...rest } = data;
      return rest;
    };
    return (
      JSON.stringify(clean(formData)) !==
        JSON.stringify(clean(initialFormData)) ||
      formData.password !== "" ||
      formData.confirmPassword !== ""
    );
  };

  const handleSubmit = async () => {
    if (formData.password !== formData.confirmPassword) {
      setSnackbar({
        open: true,
        message: "Passwords do not match",
        severity: "error",
      });
      return;
    }

    setIsSubmit(true);
    try {
      const payload = {
        username: formData.username,
        email: formData.email,
        phone: formData.phone,
        status: formData.status,
        permissions: formData.permissions,
      };

      if (formData.password) {
        payload.password = formData.password;
        payload.confirm = formData.confirmPassword;
      }

      if (cmt?.id) {
        await axios.put(
          `${process.env.REACT_APP_BACKEND_URL}/api/compound/cmt/update/${cmt.id}`,
          payload,
          { withCredentials: true }
        );
      } else {
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/compound/cmt/create`,
          { ...payload, managerId: managerId },
          { withCredentials: true }
        );
      }

      setSnackbar({
        open: true,
        message: `CMT ${cmt?.id ? "updated" : "created"} successfully`,
        severity: "success",
      });

      onSave?.();
      onClose();
    } catch (error) {
      const errorMsg = error.response?.data?.error || "Error saving CMT";
      setSnackbar({
        open: true,
        message: errorMsg,
        severity: "error",
      });
    } finally {
      setIsSubmit(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            fontWeight: "bold",
            fontSize: "1.1rem",
            bgcolor: colors.primary[600],
            color: "#fff",
            py: 1.5,
            px: 3,
          }}
        >
          <Box display="flex" justifyContent="space-between">
            <Typography>CMT Account Management</Typography>
            {cmt?.id && (
              <Chip
                label={
                  formData?.status === "active"
                    ? "Active"
                    : formData?.status === "inactive"
                    ? "Inactive"
                    : "Pending"
                }
                color={
                  formData?.status === "active"
                    ? "success"
                    : formData?.status === "inactive"
                    ? "error"
                    : "default"
                }
                size="small"
              />
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={2}>
            <TextField
              label="Username"
              name="username"
              fullWidth
              value={formData?.username}
              onChange={handleChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" mr={2}>
                    <AccountCircle />
                  </InputAdornment>
                ),
              }}
              // className="notranslate"
              sx={{
                bgcolor: isFieldChanged("username")
                  ? "rgba(255, 255, 0, 0.08)"
                  : undefined,
              }}
            />
            <TextField
              label="Phone"
              name="phone"
              fullWidth
              value={formData.phone}
              onChange={handleChange}
              InputProps={{
                className: "notranslate",
                dir:"ltr",
                startAdornment: (
                  <InputAdornment position="start" mr={2}>
                    <Phone />
                  </InputAdornment>
                ),
              }}
              sx={{
                bgcolor: isFieldChanged("phone")
                  ? "rgba(255, 255, 0, 0.08)"
                  : undefined,
              }}
            />

            <TextField
              label="Email"
              name="email"
              fullWidth
              value={formData.email}
              onChange={handleChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" mr={2}>
                    <EmailRounded />
                  </InputAdornment>
                ),
              }}
              // className="notranslate"
              sx={{
                bgcolor: isFieldChanged("email")
                  ? "rgba(255, 255, 0, 0.08)"
                  : undefined,
              }}
            />

            <TextField
              label="Password"
              type={showPassword ? "text" : "password"}
              name="password"
              fullWidth
              value={formData.password}
              onChange={handleChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" mr={2}>
                    <Lock />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end" mr={2}>
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={{ color: "#90a4ae" }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              label="Confirm Password"
              type={showPassword ? "text" : "password"}
              name="confirmPassword"
              fullWidth
              value={formData.confirmPassword}
              onChange={handleChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" mr={2}>
                    <Lock />
                  </InputAdornment>
                ),
              }}
            />

            <Box display="flex" flexDirection="column" mt={2}>
              <Typography variant="subtitle1">Permissions</Typography>
              <Box display="flex" flexWrap="wrap">
                {formData.permissions &&
                  Object.entries(formData?.permissions).map(([key, value]) => (
                    <FormControlLabel
                      key={key}
                      control={
                        <Checkbox
                          checked={value}
                          onChange={(e) =>
                            handlePermissionChange(key, e.target.checked)
                          }
                          sx={
                            isPermissionChanged(key)
                              ? {
                                  color: theme.palette.warning.main,
                                  "&.Mui-checked": {
                                    color: theme.palette.warning.main,
                                  },
                                }
                              : {}
                          }
                        />
                      }
                      label={key.replace("can", "Can ")}
                      sx={{
                        width: "50%",
                        fontWeight: isPermissionChanged(key) ? 700 : 400,
                        bgcolor: isPermissionChanged(key)
                          ? "rgba(255, 255, 0, 0.08)"
                          : "",
                      }}
                    />
                  ))}
              </Box>
            </Box>
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{ justifyContent: "space-between", px: 3, pb: 2, pt: 1 }}
        >
          <Box display="flex" gap={1}>
            {cmt.id && (
              <>
                {formData.status === "pending" && (
                  <>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<CheckCircle />}
                      onClick={() => handleStatusChange("active")}
                      sx={{ color: "#fff" }}
                      disabled={statusLoading}
                    >
                      {statusLoading ? "Activating..." : "Activate"}
                    </Button>
                    <Button
                      variant="contained"
                      color="error"
                      startIcon={<Cancel />}
                      onClick={() => handleStatusChange("inactive")}
                      sx={{ color: "#fff" }}
                      disabled={statusLoading}
                    >
                      {statusLoading ? "Inactivating..." : "Inactive"}
                    </Button>
                  </>
                )}
                {formData.status === "active" && (
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<Cancel />}
                    onClick={() => handleStatusChange("inactive")}
                    sx={{ color: "#fff" }}
                    disabled={statusLoading}
                  >
                    {statusLoading ? "Inactivating..." : "Inactive"}
                  </Button>
                )}
                {formData.status === "inactive" && (
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<CheckCircle />}
                    onClick={() => handleStatusChange("active")}
                    sx={{ color: "#fff" }}
                    disabled={statusLoading}
                  >
                    {statusLoading ? "Activating..." : "Activate"}
                  </Button>
                )}
              </>
            )}
          </Box>
          <Box display="flex" gap={1}>
            <Button
              onClick={handleSubmit}
              disabled={isSubmit || !hasFormChanged()}
              variant="contained"
            >
              {isSubmit ? "Saving..." : "Save"}
            </Button>
            <Button onClick={onClose}>Cancel</Button>
          </Box>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CMTSettingDialog;
